/**
 * DocViewSchema for Pricing Rule DocType
 *
 * Schema-driven layout for Pricing Rule detail pages.
 * ALL 89 ERPNext fields are included — nothing is omitted.
 *
 * Tab structure:
 * - Details: Basic info, storefront, apply-on scope, party, qty/amount
 * - Pricing: Price discount, product discount, margin, discount-on-other
 * - Advanced: Stacking, validation, dynamic condition, reference
 * - Meta: System/audit fields
 *
 * Create view: 3-step wizard (type picker → discount config → scope/validity)
 */

import type { DocViewSchema } from "./types";

export const pricingRuleDocViewSchema: DocViewSchema = {
  doctype: "Pricing Rule",
  views: {
    experience: {
      style: "shopify",

      sections: [
        // ===== Details Tab =====
        {
          id: "basic",
          label: "Basic Information",
          description: "Pricing rule identification and application settings",
          layout: "two-column",
          // `disable` is omitted here — it's the SAME field as the sidebar master
          // switch "เปิดใช้งานคูปอง" (inverted: switch on ⇒ disable 0). Managed
          // there to avoid a duplicate control; raw field kept in system/?generic=1.
          // company / selling / buying moved to the bottom "conditions" section
          // (PricingRuleConditionsWidget) — one collapsible block instead of the
          // two generic sub-cards "Period Settings" + "Party Information".
          fields: [
            "name",
            "title",
            "apply_on",
            "price_or_product_discount",
          ],
        },
        // "Storefront & Coupon" (coupon_code_based / storefront_enabled /
        // storefront_show_on_detail) is intentionally omitted here — these three
        // are managed as live switches in the custom right sidebar's Storefront
        // card (PricingRuleDetailSidebar). The raw fields remain in the `system`
        // view (?generic=1) as the escape hatch.
        {
          id: "apply-rule-on",
          label: "Apply Rule On",
          description: "Items, item groups, or brands this rule applies to",
          layout: "one-column",
          // Friendly product-card editor (thumbnail + name, add/remove) instead of
          // the raw ERP child-table grid; advanced flags tucked in a disclosure.
          widgetId: "PricingRuleAppliesToWidget",
          fields: [
            "items",
            "item_groups",
            "brands",
            "warehouse",
            "mixed_conditions",
            "is_cumulative",
          ],
        },
        {
          // The reward — calm detail-native editor (segmented type + value), NOT the
          // create-wizard form. See PricingRuleRewardWidget.
          id: "reward",
          label: "Reward",
          description: "What the customer gets",
          layout: "one-column",
          widgetId: "PricingRuleRewardWidget",
          fields: [
            "price_or_product_discount",
            "rate_or_discount",
            "discount_percentage",
            "discount_amount",
            "rate",
            "apply_discount_on",
            "max_discount_amount",
            "for_price_list",
            "same_item",
            "free_item",
            "free_qty",
            "custom_shipping_discount_type",
            "custom_shipping_discount_value",
            "custom_is_embroidery_coupon",
          ],
        },
        {
          // Audience + stacking/priority + advanced (discount-on-other, recursion,
          // threshold, margin) via the friendly PricingRuleTargetingWidget.
          id: "party",
          label: "Applicable For",
          description: "Who can use it & how it behaves",
          layout: "one-column",
          widgetId: "PricingRuleTargetingWidget",
          fields: [
            "applicable_for",
            "customer",
            "customer_group",
            "territory",
            "campaign",
            "apply_multiple_pricing_rules",
            "priority",
            "other_item_code",
            "is_recursive",
            "recurse_for",
            "threshold_percentage",
            "margin_type",
            "margin_rate_or_amount",
          ],
        },
        // "Quantity & Amount" (min_qty / max_qty / min_amt / max_amt) is omitted —
        // min_amt is the Promotion Summary "ยอดขั้นต่ำ" row, and min_qty/max_qty/max_amt
        // are edited in the Apply-Rule-On widget (quantity range + money cap). Raw
        // fields remain in the system/?generic=1 view.
        {
          id: "validity",
          label: "Currency",
          description: "Rule currency",
          layout: "two-column",
          // valid_from/valid_upto (Promotion Summary date range) and priority
          // (Audience & options widget) are edited in the custom UI; kept in ?generic=1.
          fields: [
            "currency",
          ],
        },
        {
          // "Period Settings" (company) + "Party Information" (selling / buying)
          // combined into ONE collapsible block, kept LAST on the page.
          id: "conditions",
          label: "More settings",
          description: "Company and selling/buying scope",
          layout: "one-column",
          widgetId: "PricingRuleConditionsWidget",
          fields: [
            "company",
            "selling",
            "buying",
          ],
        },

        // NOTE: The five "Pricing Tab" sections (price-discount, product-discount,
        // margin, discount-on-other, shipping-discount) were removed from the
        // experience view — they were orphan section objects referenced by NO
        // experience tab (the "Pricing" tab was removed). Every field now lives in
        // PricingRuleRewardWidget / PricingRuleTargetingWidget, and the raw fields
        // remain in the `system` view (?generic=1) as the escape hatch.

        // ===== Advanced Tab =====
        {
          id: "advanced-settings",
          label: "Advanced Settings",
          description: "Stacking, validation, and threshold settings",
          layout: "two-column",
          // apply_multiple_pricing_rules + threshold_percentage → "Audience & options"
          // widget. The rest are developer/edge fields, kept here.
          fields: [
            "apply_discount_on_rate",
            "validate_applied_rule",
            "rule_description",
            "apply_on_prev_doc",
          ],
        },
        {
          id: "dynamic-condition",
          label: "Dynamic Condition",
          description: "Python expression for advanced filtering",
          layout: "one-column",
          fields: [
            "condition",
          ],
        },
        {
          id: "reference",
          label: "Reference",
          description: "Promotional scheme reference",
          layout: "two-column",
          fields: [
            "promotional_scheme",
            "promotional_scheme_id",
          ],
        },

        // ===== Meta Tab =====
        {
          id: "audit",
          label: "System Information",
          description: "Audit and system fields",
          layout: "two-column",
          fields: [
            "owner",
            "creation",
            "modified_by",
            "modified",
          ],
        },
      ],

      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: [
            "basic",
            "apply-rule-on",
            "reward",
            "party",
            "validity",
            // Company + selling/buying — one collapsible block, kept last.
            "conditions",
          ],
        },
        {
          // The "Pricing" tab is removed — price/product-discount, margin,
          // discount-on-other, and shipping-discount are all edited in the custom
          // Reward / Audience widgets now. Raw fields remain in ?generic=1.
          id: "advanced",
          label: "Advanced",
          sectionIds: [
            "advanced-settings",
            "dynamic-condition",
            "reference",
          ],
        },
        {
          id: "meta",
          label: "Meta",
          sectionIds: ["audit"],
        },
      ],

      quickActions: [],
      connectionGroups: ["pricing"],
    },

    // Create view: guided wizard for new pricing rules
    create: {
      style: "shopify",
      sections: [
        {
          id: "pricing-rule-type-picker",
          label: "Pricing Rule Type",
          description: "Select what kind of pricing rule to create",
          widgetId: "PricingRuleTypePickerWidget",
        },
        {
          // Reuse the SAME calm reward editor as the detail page — covers price
          // discount, free item/BOGO, AND the perks (free shipping / free
          // personalization) the old DiscountWidget step was missing.
          id: "pricing-rule-discount",
          label: "Reward",
          description: "What the customer gets",
          widgetId: "PricingRuleRewardWidget",
        },
        {
          id: "pricing-rule-scope",
          label: "Scope & Validity",
          description: "Set conditions, validity, and advanced options",
          widgetId: "PricingRuleScopeWidget",
        },
        {
          // Audience + stacking/priority + advanced levers — same widget as detail.
          id: "pricing-rule-audience",
          label: "Audience & options",
          description: "Who can use it & how it behaves",
          widgetId: "PricingRuleTargetingWidget",
        },
      ],
    },

    // System view: raw/admin layout, field-focused — ALL fields
    system: {
      sections: [
        {
          id: "basic",
          label: "Basic",
          layout: "two-column",
          fields: [
            "name",
            "title",
            "disable",
            "company",
            "selling",
            "buying",
            "apply_on",
            "price_or_product_discount",
            "coupon_code_based",
            "storefront_enabled",
            "storefront_show_on_detail",
          ],
        },
        {
          id: "apply-rule-on",
          label: "Apply Rule On",
          layout: "one-column",
          fields: [
            "items",
            "item_groups",
            "brands",
            "warehouse",
            "mixed_conditions",
            "is_cumulative",
          ],
        },
        {
          id: "party",
          label: "Party",
          layout: "two-column",
          fields: [
            "applicable_for",
            "customer",
            "customer_group",
            "territory",
            "sales_partner",
            "campaign",
            "supplier",
            "supplier_group",
          ],
        },
        {
          id: "qty-amount",
          label: "Quantity & Amount",
          layout: "two-column",
          fields: [
            "min_qty",
            "max_qty",
            "min_amt",
            "max_amt",
          ],
        },
        {
          id: "discount-on-other",
          label: "Discount on Other Item",
          layout: "two-column",
          fields: [
            "apply_rule_on_other",
            "other_item_code",
            "other_item_group",
            "other_brand",
          ],
        },
        {
          id: "price-discount",
          label: "Price Discount",
          layout: "two-column",
          fields: [
            "rate_or_discount",
            "rate",
            "discount_percentage",
            "discount_amount",
            "apply_discount_on",
            "max_discount_amount",
            "for_price_list",
          ],
        },
        {
          id: "shipping-discount",
          label: "Shipping Discount",
          layout: "two-column",
          fields: [
            "custom_shipping_discount_type",
            "custom_shipping_discount_value",
          ],
        },
        {
          id: "product-discount",
          label: "Product Discount",
          layout: "two-column",
          fields: [
            "same_item",
            "free_item",
            "free_qty",
            "free_item_rate",
            "free_item_uom",
            "round_free_qty",
            "dont_enforce_free_item_qty",
            "is_recursive",
            "recurse_for",
            "apply_recursion_over",
          ],
        },
        {
          id: "margin",
          label: "Margin",
          layout: "two-column",
          fields: [
            "margin_type",
            "margin_rate_or_amount",
          ],
        },
        {
          id: "validity",
          label: "Validity",
          layout: "two-column",
          fields: [
            "valid_from",
            "valid_upto",
            "currency",
            "has_priority",
            "priority",
          ],
        },
        {
          id: "advanced",
          label: "Advanced",
          layout: "two-column",
          fields: [
            "apply_multiple_pricing_rules",
            "apply_discount_on_rate",
            "threshold_percentage",
            "validate_applied_rule",
            "rule_description",
            "apply_on_prev_doc",
            "condition",
          ],
        },
        {
          id: "reference",
          label: "Reference",
          layout: "two-column",
          fields: [
            "promotional_scheme",
            "promotional_scheme_id",
          ],
        },
        {
          id: "audit",
          label: "System Information",
          layout: "two-column",
          fields: [
            "owner",
            "creation",
            "modified_by",
            "modified",
          ],
        },
      ],
      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: [
            "basic",
            "apply-rule-on",
            "party",
            "qty-amount",
            "discount-on-other",
            "price-discount",
            "shipping-discount",
            "product-discount",
            "margin",
            "validity",
            "advanced",
            "reference",
          ],
        },
        {
          id: "meta",
          label: "Meta",
          sectionIds: ["audit"],
        },
      ],
    },
  },
};
